/**
 * Landing pages — a galeria. Cada card mostra a página DE VERDADE em
 * miniatura (mesma função de render da URL pública), não uma abstração:
 * páginas de captação se distinguem pela cara, não pelo título, que costuma
 * ser parecido entre todas. Editar abre /landing/<slug> — URL própria, que
 * sobrevive a F5 e dá para mandar no chat.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { api, type Lista } from '../../api/cliente'
import { Botao, Cartao, Carregando, Etiqueta, Vazio, formatarData } from '../../componentes/ui'
import { Miniatura } from './Miniatura'
import { NovaPagina } from './NovaPagina'
import { paraRender, type LandingPagina } from './tipos'

const URL_API = import.meta.env.VITE_API_URL ?? ''

export function GaleriaLanding() {
  const qc = useQueryClient()
  const navegar = useNavigate()
  const [criando, setCriando] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['landings'],
    queryFn: () => api.get<Lista<LandingPagina>>('/api/v1/landing'),
  })

  const despublicar = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/landing/${id}/despublicar`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['landings'] }),
  })

  if (isLoading) return <Carregando />

  return (
    <div className="space-y-6">
      <Cartao
        titulo="Landing pages"
        descricao="Páginas de captação. Cada uma renderiza sem depender de recurso externo, e os pixels só carregam depois do consentimento."
        acoes={<Botao onClick={() => setCriando(true)}>Nova página</Botao>}
      >
        {data?.dados.length ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.dados.map((p) => (
              <div
                key={p.id}
                className="flex flex-col overflow-hidden rounded-xl border border-[color:var(--color-borda)] transition hover:border-marca-300 hover:shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => void navegar(`/landing/${p.slug}`)}
                  aria-label={`Abrir ${p.titulo}`}
                  className="block text-left"
                >
                  <Miniatura pagina={paraRender(p)} />
                </button>

                <div className="flex flex-1 flex-col gap-2 border-t border-[color:var(--color-borda)] p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="min-w-0 flex-1 truncate font-medium text-slate-900">{p.titulo}</p>
                    <Etiqueta tom={p.publicada ? 'sucesso' : 'neutro'}>
                      {p.publicada ? 'no ar' : 'rascunho'}
                    </Etiqueta>
                  </div>
                  <p className="truncate font-mono text-xs text-slate-400">/lp/{p.slug}</p>
                  <p className="text-xs text-slate-400">
                    v{p.versao} · {formatarData(p.atualizadoEm)}
                  </p>

                  <div className="mt-auto flex flex-wrap gap-2 pt-2">
                    <Link to={`/landing/${p.slug}`}>
                      <Botao variante="secundario" tamanho="sm">
                        Editar
                      </Botao>
                    </Link>
                    {p.publicada && (
                      <>
                        <a
                          href={`${URL_API}/publico/lp/${p.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center rounded-lg border border-[color:var(--color-borda)] px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50"
                        >
                          Ver no ar
                        </a>
                        <Botao
                          variante="secundario"
                          tamanho="sm"
                          onClick={() => despublicar.mutate(p.id)}
                        >
                          Despublicar
                        </Botao>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Vazio
            titulo="Nenhuma landing page ainda"
            descricao="Crie a primeira: descreva o objetivo e a IA monta a estrutura para você ajustar."
            acao={<Botao onClick={() => setCriando(true)}>Nova página</Botao>}
          />
        )}
      </Cartao>

      <NovaPagina aberto={criando} aoFechar={() => setCriando(false)} />
    </div>
  )
}
